/**
 * Centralized client-side export utilities.
 *
 * Regulated export generation is still expected to run through backend APIs;
 * this helper is only for assembling client-side CSV payloads that are
 * already held in memory for display.
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Download a server-generated, already-authorized export without re-creating it in the browser. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export interface CsvOptions {
  /** Prevent spreadsheet formula execution for user-controlled string cells. */
  formulaInjectionProtection?: boolean;
  /**
   * Descriptor-authorized column order. This preserves a useful header row
   * when a scoped list has no rows, without inferring fields from data that
   * is absent or outside the current page.
   */
  headers?: readonly string[];
}

const PURE_NUMBER = /^[-+]?\d+(?:\.\d+)?$/;
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

function protectFormula(value: string): string {
  if (PURE_NUMBER.test(value.trim())) return value;
  return FORMULA_PREFIX.test(value) ? `'${value}` : value;
}

export function toCsv(rows: Record<string, unknown>[], options: CsvOptions = {}): string {
  const headers = options.headers ?? (rows[0] ? Object.keys(rows[0]) : []);
  if (headers.length === 0) return '';
  const protect = options.formulaInjectionProtection !== false;
  const escape = (value: unknown) => {
    const raw = value == null ? '' : String(value);
    const safe = protect ? protectFormula(raw) : raw;
    if (safe.includes(',') || safe.includes('\n') || safe.includes('"')) {
      return `"${safe.replace(/"/g, '""')}"`;
    }
    return safe;
  };
  return [
    headers.join(','),
    ...rows.map(row => headers.map(header => escape(row[header])).join(',')),
  ].join('\n');
}
